import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";
import { useHistory } from "react-router-dom";
import * as sup from "superstruct";

import { DetailLayout } from "@/layouts/DetailLayout/DetailLayout";
import { useAuthContext } from "@/modules/auth/hooks/useAuthContext";
import { Form } from "@/modules/form/components/Form/Form";
import { notEmpty } from "@/modules/form/utils/structs";
import { authenticatedRouting } from "@/modules/router/routings/authenticatedRouting";
import { ButtonWithArrow } from "@/modules/theme/components/ButtonWithArrow/ButtonWithArrow";
import { DetailLayoutBox } from "@/modules/theme/components/DetailLayoutBox";
import { Loader } from "@/modules/theme/components/Loader/Loader";
import { useTheme } from "@/modules/theme/hooks/useTheme";
import { recompose } from "@/utils/structs/recompose";

import { useGetTutorialProgressDetailsQuery } from "../TutorialDetailsPage/hooks/useGetTutorialProgressDetailsQuery";

import { createTutorialTestPageStyles } from "./createTutorialTestPageStyles";
import { Questions } from "./Questions";
import { useCompleteTutorialTestMutation } from "./useCompleteTutorialTestMutation";

const tutorialTestStruct = sup.object({
  answers: sup.array(
    sup.object({
      questionId: recompose(sup.string(), notEmpty),
      answerIndex: recompose(sup.string(), notEmpty),
    })
  ),
});

export type TutorialTestStructType = sup.Infer<typeof tutorialTestStruct>;

const translationNamespace = "pages/TutorialTestPage";

export const TutorialTestPage = () => {
  const params = authenticatedRouting.useRouteParams("tutorialTest");

  const classes = createTutorialTestPageStyles(useTheme());
  const history = useHistory();
  const { t } = useTranslation(translationNamespace);
  const { userCompanyId } = useAuthContext();
  const {
    data: tutorialProgressDetails,
    isLoading: areTutorialProgressDetailsLoading,
  } = useGetTutorialProgressDetailsQuery(params?.id);
  const completeTutorialTestMutation = useCompleteTutorialTestMutation();

  const handleSubmit = useCallback(
    ({ answers }: TutorialTestStructType) => {
      if (tutorialProgressDetails && params && userCompanyId) {
        completeTutorialTestMutation.mutate({
          answers,
          companyId: userCompanyId,
          tutorialId: params.id,
          tutorialVersion: tutorialProgressDetails.updatedAt,
        });
      }
    },
    [
      completeTutorialTestMutation,
      userCompanyId,
      params,
      tutorialProgressDetails,
    ]
  );

  const defaultValues = useMemo(() => {
    return {
      answers: tutorialProgressDetails?.testQuestions.map((question) => ({
        questionId: question?.id,
        answerIndex: "",
      })),
    };
  }, [tutorialProgressDetails]);

  return (
    <DetailLayout
      onBackButtonClick={() =>
        history.push(authenticatedRouting.buildUrl("tutorials"))
      }
      variant="tutorial"
      heading={
        areTutorialProgressDetailsLoading ? (
          <Skeleton />
        ) : (
          tutorialProgressDetails &&
          `${t("Test")} - ${tutorialProgressDetails?.name}`
        )
      }
    >
      <DetailLayoutBox className={classes.detailLayoutBox}>
        {areTutorialProgressDetailsLoading ? (
          <div className={classes.loaderWrapper}>
            <Loader size={70} variant="primary" />
          </div>
        ) : (
          tutorialProgressDetails && (
            <Form
              onValidSubmit={handleSubmit}
              struct={tutorialTestStruct}
              defaultValues={defaultValues}
            >
              {() => (
                <>
                  <Questions
                    questions={tutorialProgressDetails.testQuestions}
                  />
                  <ButtonWithArrow
                    type="submit"
                    className={classes.submitButton}
                    isDisabled={
                      completeTutorialTestMutation.status === "loading"
                    }
                    isLoading={
                      completeTutorialTestMutation.status === "loading"
                    }
                  >
                    {t("Check the answers")}
                  </ButtonWithArrow>
                </>
              )}
            </Form>
          )
        )}
      </DetailLayoutBox>
    </DetailLayout>
  );
};
